import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigation } from 'expo-router';
import type { NavigationProp, EventArg } from 'expo-router/react-navigation';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ERROR_MESSAGES } from '@/constants/errorMessages';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useGroupedInquiriesEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useHaptic } from '@/hooks/useHaptic';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useRefreshOnFocus } from '@/hooks/useRefreshOnFocus';
import { useToaster } from '@/hooks/useToaster';
import { createInquiryV2 } from '@/services/createInquiryV2';
import { createUploadUrlV2, type CreateUploadUrlV2Body } from '@/services/createUploadUrlV2';
import { deleteDraftV2 } from '@/services/deleteDraftV2';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { getDraftV2QueryOptions } from '@/services/getDraftV2';
import { getDraftsV2QueryOptions } from '@/services/getDraftsV2';
import { userIdAtom } from '@/services/globalState';
import { setInquiryDraftV2 } from '@/services/setInquiryDraftV2';
import { uploadFileToPresignedUrl } from '@/services/uploadFileToPresignedUrl';
import { segmentClient } from '@/util/analytics';
import { isPendingOAuthUserId } from '@/util/authTokens';
import { getFileContent } from '@/util/getFileContent';
import { invalidateUnreadInquiriesDebounced } from '@/util/inboxInvalidation';
import { queryClient } from '@/util/queryClient';

const createDraftInquirySchema = (hasPrimaryExpert: boolean) =>
  z
    .object({
      enableExpertSelection: z.boolean().optional(),
      expertIds: hasPrimaryExpert
        ? z.array(z.number()).max(2, { message: 'Maximum 2 additional experts allowed' })
        : z.array(z.number()).max(3, { message: 'Maximum 3 experts allowed' }),
      expertNotes: z.record(z.string()).optional().default({}),
      cancerFocusId: z.string().min(1, { message: 'Cancer type is required' }),
      message: z.string().min(1, { message: 'Message is required' }),
      noPII: z.boolean().refine((value: boolean) => value === true, {
        message: 'Confirmation is required',
      }),
      anonymous: z.boolean().optional().default(false),
      files: z
        .array(
          z.object({
            name: z.string(),
            uri: z.string(),
            mimeType: z.string(),
            size: z.number(),
            s3Key: z.string().optional(),
            file: z.any().optional(),
          })
        )
        .max(3, { message: 'Maximum 3 files allowed' })
        .optional(),
      attachmentNoPHI: z.boolean().optional(),
    })
    .refine(
      (data) => {
        if (data.files && data.files.length > 0) {
          return data.attachmentNoPHI === true;
        }

        return true;
      },
      {
        message: 'PHI confirmation is required when uploading files',
        path: ['attachmentNoPHI'],
      }
    );

export type InquiryDraftFormData = z.infer<ReturnType<typeof createDraftInquirySchema>>;

interface UseInquiryFormProps {
  expertId?: string;
  currentDraftId?: string;
  selectedCancerFocusId: string;
  referenceConversationId?: string;
  onSuccess: (expertIds: number[], cancerFocusId: number, inquiryId?: number) => void;
}

function getEmptyFormValues(cancerFocusId: string | undefined): InquiryDraftFormData {
  return {
    enableExpertSelection: false,
    expertIds: [],
    expertNotes: {},
    cancerFocusId: cancerFocusId || '',
    message: '',
    noPII: false,
    anonymous: false,
    files: [],
    attachmentNoPHI: false,
  };
}

/**
 * Form Logic Hook for Medical Inquiry Drafts
 *
 * @overview
 * Encapsulates all form state management, validation, auto-saving, and submission logic
 * for the medical inquiry form. Handles both single-expert and multi-expert workflows.
 *
 * @behavior
 * - Form state management (react-hook-form + zod), auto-save with debounce, draft loading/hydration
 * - Submission with error handling, analytics, and real-time status feedback
 * - Avoids caret jumps by hydrating draft message only when field is pristine
 *
 * **Draft ID Synchronization:**
 * - Updates draftId state when currentDraftId prop changes; resets hasLoadedDraft flag
 * - Resets form to defaults when navigating to new inquiry (no draftId)
 * - Cancels pending queries when draftId changes or is cleared to prevent stale data
 *
 * **Draft Deletion & Cache:**
 * - Clears draftId immediately in onMutate to disable query and prevent refetches
 * - Cancels in-flight queries, removes from cache, and updates drafts list
 * - Draft query uses refetchOnWindowFocus: false; manual refetching via useRefreshOnFocus
 * - Cache updates use deep merge for content object to preserve message field
 *
 * **Draft Expert Selection:**
 * - The chosen experts are saved as `expertIds` and restored with the checkbox ticked. On a
 *   Directory Inquiry they are the additional experts; the primary is `expertId`
 * - Per-expert notes are not saved
 * - Directory Inquiry: Saves expertId (primary) for routing only; Inquiry: expertId is null
 * - Routing: Drafts with expertId → Directory Inquiry, without → Inquiry
 *
 * **File Handling:**
 * - Uploads during submission (not auto-save); validates PHI confirmation when present
 * - Supports up to 3 files (25 MB per file); tracks upload analytics
 * - Progress feedback: "This may take a moment...", "Uploading attachments (X/Y)...", "Submitting inquiry..."
 * - `submissionStatus` set during uploads (before `isSubmitting`) to prevent double submissions
 *
 * **Validation:**
 * - Required: cancer type, message, PII confirmation; conditional PHI confirmation when files attached
 * - Expert limit: max 2 additional experts
 *
 * **Submission Format:**
 * - Directory Inquiry: `expertId` (primary, required), `expertIds` [primary, ...additional] (API requires primary first)
 * - Inquiry: `expertId` is null, `expertIds` contains all selected experts
 */
export function useInquiryForm({
  expertId,
  currentDraftId,
  selectedCancerFocusId,
  referenceConversationId,
  onSuccess,
}: UseInquiryFormProps) {
  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const isInquiryMultiExpertNotesEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_MULTI_EXPERT_NOTES);

  const isDirectoryInquiry = !!expertId;

  const navigation = useNavigation() as NavigationProp<ReactNavigation.RootParamList>;

  const toast = useToaster();

  const haptic = useHaptic();

  const userId = useAtomValue(userIdAtom);

  const { isPharmaUser } = useCognitoGroups();

  const [draftId, setDraftId] = useState(currentDraftId || '');

  const [hasLoadedDraft, setHasLoadedDraft] = useState(false);

  const [submissionStatus, setSubmissionStatus] = useState<string>('');

  const shouldSaveDraft = useRef(true);

  const videoInfoRef = useRef({ hasVideoAttachments: false, videoAttachmentCount: 0 });

  const prevCurrentDraftIdRef = useRef<string | undefined>(currentDraftId);

  const { data: currentUser } = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    enabled: !!userId && !isPendingOAuthUserId(userId),
  });

  const draftQuery = useQuery({
    ...getDraftV2QueryOptions({ draftUuid: draftId, userId }),
    enabled: !!draftId,
    refetchOnWindowFocus: false,
  });

  const { refetch: refetchDraftQuery } = draftQuery;

  /**
   * Simple refetch wrapper that only refetches if draftId exists.
   * When draftId is cleared (after deletion), this becomes a no-op.
   *
   * Depends on the stable refetch fn (not the whole draftQuery object),
   * so the useFocusEffect inside useRefreshOnFocus doesn't re-fire on
   * every render and storm /v2/drafts/{uuid} on every keystroke.
   */
  const refetchDraft = useCallback(() => {
    if (draftId) {
      refetchDraftQuery();
    }
  }, [draftId, refetchDraftQuery]);

  useRefreshOnFocus(refetchDraft);

  const draftInquirySchema = useMemo(
    () => createDraftInquirySchema(isDirectoryInquiry),
    [isDirectoryInquiry]
  );

  const form = useForm<InquiryDraftFormData>({
    defaultValues: getEmptyFormValues(selectedCancerFocusId),
    resolver: zodResolver(draftInquirySchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    const prevPropDraftId = prevCurrentDraftIdRef.current || '';

    const nextPropDraftId = currentDraftId || '';

    if (prevPropDraftId !== nextPropDraftId) {
      prevCurrentDraftIdRef.current = currentDraftId;

      setHasLoadedDraft(false);

      if (prevPropDraftId && !nextPropDraftId) {
        const oldDraftQueryKey = getDraftV2QueryOptions({
          draftUuid: prevPropDraftId,
          userId,
        }).queryKey;

        queryClient.cancelQueries({ queryKey: oldDraftQueryKey });
      }

      setDraftId(nextPropDraftId);

      if (!nextPropDraftId && prevPropDraftId) {
        form.reset(getEmptyFormValues(selectedCancerFocusId));
      }
    }
  }, [currentDraftId, selectedCancerFocusId, form]);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid, dirtyFields },
    getValues,
    setValue,
    trigger,
  } = form;

  const formValues = useWatch({ control });

  useEffect(() => {
    if (formValues.expertIds !== undefined) {
      trigger();
    }
  }, [formValues.expertIds?.length, trigger]);

  /**
   * Returns expertId to save for routing (Directory Inquiry) or null (Inquiry).
   */
  const getExpertIdForDraft = useCallback(() => {
    if (isDirectoryInquiry) {
      return Number(expertId);
    }

    return null;
  }, [isDirectoryInquiry, expertId]);

  const getDraftSupportedFields = useCallback(() => {
    const values = getValues();

    return {
      cancerFocusId: values.cancerFocusId,
      message: values.message,
      noPII: values.noPII,
      anonymous: values.anonymous || false,
      expertIds: values.expertIds ?? [],
    };
  }, [getValues]);

  useEffect(() => {
    if (selectedCancerFocusId && !formValues.cancerFocusId && !hasLoadedDraft) {
      setValue('cancerFocusId', selectedCancerFocusId, { shouldDirty: false });
    }
  }, [selectedCancerFocusId, formValues.cancerFocusId, hasLoadedDraft, setValue]);

  /**
   * Saves the draft and patches both caches with what the API returned. The drafts list is
   * refetched only when the save created a draft, since a draft that was already listed has just
   * been patched in place and the autosave runs after every pause in typing.
   */
  const saveInquiryDraftMutation = useMutation({
    mutationFn: setInquiryDraftV2,
    onSuccess: (data, variables) => {
      if (!data?.uuid) {
        return;
      }

      const draftsQueryKey = getDraftsV2QueryOptions(userId).queryKey;

      queryClient.cancelQueries({ queryKey: draftsQueryKey });

      queryClient.setQueryData(draftsQueryKey, (prev) => {
        if (prev) {
          return prev.map((draft) => {
            if (draft.uuid === data.uuid) {
              return { ...draft, ...data };
            }

            return draft;
          });
        }

        return prev;
      });

      if (variables.draftUuid !== data.uuid) {
        queryClient.invalidateQueries({ queryKey: draftsQueryKey, exact: true });
      }

      queryClient.setQueryData(
        getDraftV2QueryOptions({ draftUuid: data.uuid, userId }).queryKey,
        (prev) => {
          if (prev) {
            return {
              ...prev,
              ...data,
              content: {
                ...prev.content,
                ...data.content,
              },
            };
          }

          return data;
        }
      );
    },
  });

  /**
   * Delete draft mutation with cleanup to prevent 404 errors.
   */
  const deleteInquiryDraftMutation = useMutation({
    mutationFn: deleteDraftV2,
    onMutate: (currentDraftId) => {
      if (draftId === currentDraftId) {
        setDraftId('');
      }

      const draftQueryKey = getDraftV2QueryOptions({ draftUuid: currentDraftId, userId }).queryKey;

      queryClient.cancelQueries({ queryKey: draftQueryKey });
    },
    onSuccess: (_, currentDraftId) => {
      const draftsQueryKey = getDraftsV2QueryOptions(userId).queryKey;

      const draftQueryKey = getDraftV2QueryOptions({ draftUuid: currentDraftId, userId }).queryKey;

      queryClient.removeQueries({ queryKey: draftQueryKey });

      queryClient.setQueryData(draftsQueryKey, (prev) => {
        if (Array.isArray(prev)) {
          return prev.filter((draft) => draft.uuid !== currentDraftId);
        }

        return [];
      });
    },
    onError: () => {
      toast.showError({ title: 'Unable to delete draft' });
    },
  });

  const createInquiryMutation = useMutation({
    mutationFn: createInquiryV2,
    onSuccess: (newInquiry, variables) => {
      setSubmissionStatus('');

      const expertIds = variables.expertIds || [];

      const expertCount = expertIds.length;

      const hasAttachments = !!(variables.files && variables.files.length > 0);

      const attachmentCount = variables.files?.length || 0;

      const expertMessageCount = Object.keys(variables.expertNotes ?? {}).length;

      const source = expertId ? 'directory' : 'inquiry';

      const hasAdditionalExperts = expertId ? expertCount > 1 : false;

      segmentClient.track(ANALYTICS_EVENTS.INQUIRY_SUBMITTED, {
        source,
        anonymous: variables.anonymous || false,
        expertIds,
        expertId: variables.expertId || null,
        expertCount,
        cancerFocusId: variables.cancerFocusId,
        hasAttachments,
        attachmentCount,
        hasAdditionalExperts,
        hasExpertMessages: expertMessageCount > 0,
        expertMessageCount,
        inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
        anonymousFanOut: variables.anonymousFanOut,
      });

      queryClient
        .invalidateQueries({
          queryKey: ['inquiriesV2'],
          refetchType: 'active',
        })
        .catch(() => undefined);

      invalidateUnreadInquiriesDebounced(queryClient, 0);

      if (draftId) {
        deleteInquiryDraftMutation.mutate(draftId);
      }

      shouldSaveDraft.current = false;

      onSuccess(expertIds as number[], variables.cancerFocusId, newInquiry?.id);
    },
    onError: (error) => {
      setSubmissionStatus('');

      segmentClient.track(ANALYTICS_EVENTS.INQUIRY_FAILED_SUBMIT, {
        sender: userId,
        sender_email: currentUser?.email || '',
        sender_role: currentUser?.userType || '',
        error: JSON.stringify(error),
      });

      toast.showError({ title: 'Unable to submit inquiry' });
    },
  });

  /**
   * Saves the draft once typing pauses. A save still in flight re-arms the timer instead of
   * sending a second one beside it: before the first answers, both would read the same missing
   * draft id and create two drafts.
   */
  const autoSaveDraftInquiry = useDebouncedCallback(
    useCallback(() => {
      if (saveInquiryDraftMutation.isPending) {
        autoSaveDraftInquiry();

        return;
      }

      if (isDirty && userId) {
        const draftFields = getDraftSupportedFields();

        const expertIdToSave = getExpertIdForDraft();

        saveInquiryDraftMutation.mutate(
          {
            cancerFocusId: draftFields.cancerFocusId,
            message: draftFields.message,
            noPII: draftFields.noPII,
            anonymous: draftFields.anonymous,
            expertIds: draftFields.expertIds,
            expertId: expertIdToSave,
            draftUuid: draftId,
            shouldRecreateIfMissing: () => shouldSaveDraft.current,
          },
          {
            onSuccess: (data) => {
              setDraftId(data?.uuid || '');
            },
            onError: () => {
              toast.showError({ title: 'Unable to save draft' });
            },
          }
        );
      }
    }, [
      draftId,
      isDirty,
      userId,
      saveInquiryDraftMutation,
      setDraftId,
      getDraftSupportedFields,
      getExpertIdForDraft,
      isDirectoryInquiry,
      getValues,
    ]),
    3000,
    { leading: false, trailing: true }
  );

  useEffect(() => {
    if (draftQuery.isSuccess && draftId && !hasLoadedDraft && draftQuery.data?.content) {
      const draft = draftQuery.data.content;

      const currentValues = getValues();

      const nextCancerFocusId = draft?.cancerTypeId?.toString() ?? '';

      if (currentValues.cancerFocusId !== nextCancerFocusId) {
        setValue('cancerFocusId', nextCancerFocusId, { shouldDirty: false });
      }

      const nextMessage = draft?.message ?? '';

      const shouldHydrateMessage =
        !dirtyFields?.message &&
        currentValues.message !== nextMessage &&
        (nextMessage !== '' || currentValues.message === '');

      if (shouldHydrateMessage) {
        setValue('message', nextMessage, { shouldDirty: false });
      }

      const nextNoPII = draft?.noPII ?? false;

      if (currentValues.noPII !== nextNoPII) {
        setValue('noPII', nextNoPII, { shouldDirty: false });
      }

      const nextAnonymous = draft?.anonymous ?? false;

      if (currentValues.anonymous !== nextAnonymous) {
        setValue('anonymous', nextAnonymous, { shouldDirty: false });
      }

      const nextExpertIds = draft?.expertIds ?? [];

      if (
        !dirtyFields?.expertIds &&
        currentValues.expertIds.join(',') !== nextExpertIds.join(',')
      ) {
        setValue('expertIds', nextExpertIds, { shouldDirty: false });

        setValue('enableExpertSelection', nextExpertIds.length > 0, { shouldDirty: false });
      }

      setHasLoadedDraft(true);

      trigger();
    }
  }, [
    draftQuery.isSuccess,
    draftQuery.data,
    draftId,
    hasLoadedDraft,
    setValue,
    getValues,
    dirtyFields,
    isDirectoryInquiry,
    expertId,
    trigger,
  ]);

  /**
   * Re-arms the save on every change to a field the draft keeps, so the draft follows the form
   * until typing pauses. Keyed on `isDirty` alone, as it was, the save ran once per visit, three
   * seconds after the first change: a question typed after picking a cancer type never reached
   * the draft.
   */
  useEffect(() => {
    autoSaveDraftInquiry();

    return () => autoSaveDraftInquiry.cancel();
  }, [
    isDirty,
    formValues.cancerFocusId,
    formValues.message,
    formValues.noPII,
    formValues.anonymous,
    formValues.expertIds?.join(','),
    autoSaveDraftInquiry,
  ]);

  useEffect(() => {
    const unsubscribe = navigation.addListener(
      'beforeRemove',
      (e: EventArg<'beforeRemove', true, { action: unknown }>) => {
        if (isDirty && shouldSaveDraft.current && userId) {
          const draftFields = getDraftSupportedFields();

          const expertIdToSave = getExpertIdForDraft();

          saveInquiryDraftMutation.mutate(
            {
              cancerFocusId: draftFields.cancerFocusId,
              message: draftFields.message,
              noPII: draftFields.noPII,
              anonymous: draftFields.anonymous,
              expertIds: draftFields.expertIds,
              expertId: expertIdToSave,
              draftUuid: draftId,
            },
            {
              onSuccess: () => {
                toast.showSuccess({ title: 'Draft saved' });

                navigation.dispatch(e.data.action as Parameters<typeof navigation.dispatch>[0]);
              },
            }
          );
        }
      }
    );

    return unsubscribe;
  }, [
    isDirty,
    draftId,
    userId,
    getDraftSupportedFields,
    saveInquiryDraftMutation,
    getExpertIdForDraft,
  ]);

  /**
   * Combines expertIds for submission: Directory Inquiry includes primary expert first;
   * Inquiry returns all selected experts. Removes duplicates.
   */
  const combineExpertIdsForSubmission = useCallback(
    (formExpertIds: number[] | undefined): number[] => {
      if (!isDirectoryInquiry) {
        const expertIds = formExpertIds || [];

        return Array.from(new Set(expertIds));
      }

      const primaryExpertId = Number(expertId);

      if (formExpertIds && formExpertIds.length > 0) {
        const combined = [primaryExpertId, ...formExpertIds];

        return Array.from(new Set(combined));
      }

      return [primaryExpertId];
    },
    [isDirectoryInquiry, expertId]
  );

  const isMaintenanceMode = useMaintenanceMode();

  const submitInquiry = handleSubmit(
    async (data) => {
      if (isMaintenanceMode) {
        toast.showError(ERROR_MESSAGES.INQUIRY.MAINTENANCE_MODE);

        return;
      }

      if (isPharmaUser) {
        toast.showError(ERROR_MESSAGES.INQUIRY.PHARMA_NOT_AUTHORIZED);

        return;
      }

      haptic.impactMedium();

      autoSaveDraftInquiry.cancel();

      const allExpertIds = combineExpertIdsForSubmission(data.expertIds);

      videoInfoRef.current = {
        hasVideoAttachments: data.files?.some((f) => f.mimeType.startsWith('video/')) || false,
        videoAttachmentCount:
          data.files?.filter((f) => f.mimeType.startsWith('video/')).length || 0,
      };

      const uploadedFiles: { s3Key: string; fileName: string; noPHI: true }[] = [];

      if (data.files && data.files.length > 0) {
        setSubmissionStatus('This may take a moment...');

        await new Promise((resolve) => setTimeout(resolve, 1000));

        let currentFile: (typeof data.files)[number] | undefined;

        try {
          for (let index = 0; index < data.files.length; index++) {
            const file = data.files[index];

            currentFile = file;

            setSubmissionStatus(`Uploading attachments (${index + 1}/${data.files.length}) ...`);

            const uploadUrlData = await createUploadUrlV2({
              mimeType: file.mimeType as CreateUploadUrlV2Body['mimeType'],
            });

            if (!uploadUrlData?.url || !uploadUrlData?.s3Key) {
              throw new Error(`Failed to get upload URL for ${file.name}`);
            }

            const fileContent = await getFileContent(file);

            await uploadFileToPresignedUrl({
              presignedUrl: uploadUrlData.url,
              file: fileContent,
            });

            uploadedFiles.push({
              s3Key: uploadUrlData.s3Key,
              fileName: file.name,
              noPHI: true,
            });

            segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_COMPLETED, {
              file_name: file.name,
              file_size: file.size,
              mime_type: file.mimeType,
              is_video: file.mimeType.startsWith('video/'),
              context: 'inquiry_submission',
            });
          }
        } catch (error) {
          console.error('File upload failed:', error);

          toast.showError({
            title: 'File upload failed',
            message: ERROR_MESSAGES.ATTACHMENTS.UPLOAD_FAILED.INQUIRY_FORM,
          });

          segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
            error: error instanceof Error ? error.message : 'Unknown error',
            file_size: currentFile?.size,
            mime_type: currentFile?.mimeType,
            context: 'inquiry_submission',
          });

          setSubmissionStatus('');

          return;
        }
      }

      setSubmissionStatus('Submitting inquiry...');

      const expertNotesForSubmit =
        isInquiryMultiExpertNotesEnabled && allExpertIds.length > 0 && data.expertNotes
          ? Object.fromEntries(
              allExpertIds.flatMap((id) => {
                const note = data.expertNotes[String(id)]?.trim();

                // Wrap in an extra array so flatMap yields entry tuples, not flattened key/value scalars.
                return note ? [[String(id), note] as const] : [];
              })
            )
          : undefined;

      createInquiryMutation.mutateAsync({
        cancerFocusId: Number(data.cancerFocusId),
        message: data.message,
        expertIds: allExpertIds,
        expertId: isDirectoryInquiry ? Number(expertId) : null,
        anonymous: data.anonymous || false,
        anonymousFanOut: Boolean(data.anonymous),
        noPII: data.noPII,
        files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        referenceInquiryIds: referenceConversationId ? [referenceConversationId] : undefined,
        ...(expertNotesForSubmit && Object.keys(expertNotesForSubmit).length > 0
          ? { expertNotes: expertNotesForSubmit }
          : {}),
      });
    },
    () => {
      haptic.error();
    }
  );

  const deleteDraft = (onSuccess?: () => void) => {
    if (draftId) {
      shouldSaveDraft.current = false;

      autoSaveDraftInquiry.cancel();

      deleteInquiryDraftMutation.mutate(draftId, {
        onSuccess: () => {
          toast.showSuccess({ title: 'Draft deleted' });

          onSuccess?.();
        },
      });
    }
  };

  /**
   * Empties the form for a new inquiry in place, for a composer that stays on screen after its
   * draft is deleted. Deleting stops the save made on leaving the screen, so a save cannot bring
   * the deleted draft back; this turns it on again and drops the old draft id, so the next inquiry
   * saves as a draft of its own.
   */
  const resetToEmptyForm = useCallback(() => {
    autoSaveDraftInquiry.cancel();

    setDraftId('');

    setHasLoadedDraft(false);

    form.reset(getEmptyFormValues(selectedCancerFocusId));

    shouldSaveDraft.current = true;
  }, [autoSaveDraftInquiry, form, selectedCancerFocusId]);

  /**
   * Disables automatic draft saving. Use this before redirecting to prevent
   * the beforeRemove listener from saving a duplicate draft.
   */
  const disableDraftSaving = useCallback(() => {
    shouldSaveDraft.current = false;

    autoSaveDraftInquiry.cancel();
  }, [autoSaveDraftInquiry]);

  return {
    control,
    errors,
    formValues,
    isValid,
    setValue,

    submitInquiry,
    deleteDraft,
    disableDraftSaving,
    resetToEmptyForm,

    isSubmitting: createInquiryMutation.isPending,
    isDirty,
    isDeleteDisabled: !draftQuery.data && !isDirty,
    isDeleting: deleteInquiryDraftMutation.isPending,
    submissionStatus,

    isPharmaUser,

    draftQuery,
  };
}
